import React, { useCallback, useState } from 'react';

import { Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';

import InviteUserForm from '../../components/users/InviteUserForm';
import ValidationException from '../../exceptions/ValidationException';
import User from '../../models/User';
import UserService from '../../services/user.service';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
      margin: '16px',
      maxWidth: '500px',
      overflow: 'visible',
      width: '100%',
    },
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '36px 24px',
  },
}));

const InviteUserModal = ({ open, onClose, onUserInvited }) => {
  const classes = useStyles();
  const [validationErrors, setValidationErrors] = useState(null);
  const [defaultData, setDefaultData] = useState(new User());
  const [resetData, setResetData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const userService = new UserService();

  const inviteUser = async (userData) => {
    try {
      setIsSubmitting(true);

      const result = await userService.inviteUser(userData);

      if (onUserInvited) {
        onUserInvited();
      }

      enqueueSnackbar('User invitation sent successfully', { variant: 'success' });

      return result;
    } catch (error) {
      if (error.response?.status === 422) {
        throw new ValidationException(error.response.data.errors);
      }
      enqueueSnackbar(error.message || 'Failed to invite user', { variant: 'error' });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const onInviteUser = async (formData) => {
    try {
      setResetData(false);

      await inviteUser(formData);

      setDefaultData(new User());
      setValidationErrors(null);
      setResetData(true);
      onClose();
    } catch (exception) {
      if (exception instanceof ValidationException) {
        setValidationErrors(exception.errors);
      }
    }
  };

  const handleSetExternalError = useCallback((fieldName, value) => {
    setValidationErrors((errors) => ({ ...(errors || {}), [fieldName]: value }));
  }, []);

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <div className={classes.modalContent}>
        <InviteUserForm
          resetData={resetData}
          onInviteUser={onInviteUser}
          validationErrors={validationErrors}
          defaultData={defaultData}
          isSubmitting={isSubmitting}
          onSetExternalError={handleSetExternalError}
          onCancel={onClose}
          title="Invite User"
          subtitle="Please enter the details of the user you want to invite"
        />
      </div>
    </Dialog>
  );
};

export default InviteUserModal;
